import React from 'react';
import Box from '../Box/Box';
import Grid from '../Grid/Grid';
import { Body1, Body2, Head6, Subheading1 } from '../Typography/Typography';
import { rem } from 'polished';
import Field from '../Field/Field';
import { useForm } from 'react-hook-form';
import { EMAIL_REGEX } from '../../../config/constants';
import Button from '../Button/Button';
import ChevronDown from '../../images/icons/chevron-down.svg';
import CloseIcon from '../../images/icons/close.svg';
import SuccessMarkIcon from '../../images/icons/success-mark.svg';
import { css } from 'styled-components';
import { useState } from 'react';
import { subscribeToOurBlog } from '../../services/blog';

export const SubscribeToBlogSuccessMessage = ({ isInsideBox = false }) => {
    const Heading = isInsideBox ? Subheading1 : Head6;
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            maxWidth={isInsideBox ? rem(250) : 'auto'}
            mx="auto"
        >
            <Box
                display="flex"
                alignItems={isInsideBox ? 'center' : 'start'}
                gap={rem(isInsideBox ? 14 : 10)}
                flexDirection={isInsideBox ? 'column' : 'row'}
            >
                <Box
                    as={SuccessMarkIcon}
                    size={rem(26)}
                    mt={rem(4)}
                    transform={isInsideBox ? 'scale(1.2)' : 'initial'}
                />

                <Box textAlign={isInsideBox ? 'center' : 'start'}>
                    <Heading mb={rem(8)} fontWeight="600">
                        Thank you for subscribing to our blog
                    </Heading>
                    <Body2>You will get notified once we post something!</Body2>
                </Box>
            </Box>
        </Box>
    );
};

export const SubscribeToBlog = () => {
    const { register, errors, handleSubmit } = useForm();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const toggle = () => {
        setIsCollapsed((prevState) => !prevState);
    };

    const handleOnSubmit = (data) => {
        setIsLoading(true);
        subscribeToOurBlog(data)
            .then(() => {
                setIsSubmitted(true);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Box
            backgroundColor="primary.100"
            pt={{
                _: rem(16),
                md: isSubmitted ? rem(16) : rem(isCollapsed ? 0 : 68),
            }}
            pb={{
                _: rem(16),
                md: isSubmitted ? rem(16) : rem(isCollapsed ? 0 : 32),
            }}
            position="sticky"
            bottom={0}
            hidden={isHidden}
        >
            {isSubmitted ? (
                <SubscribeToBlogSuccessMessage />
            ) : (
                <>
                    <Box
                        as="button"
                        width={rem(60)}
                        height={rem(36)}
                        mb={rem(36)}
                        backgroundColor="primary.300"
                        display={{ _: 'none', md: 'flex' }}
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={
                            isCollapsed ? '4px 4px 0 0' : '0 0 4px 4px'
                        }
                        position="absolute"
                        top={0}
                        left="50%"
                        transform={`translate(-50%,${
                            isCollapsed ? '-100%' : '0'
                        })`}
                        cursor="pointer"
                        css={css`
                            :active {
                                background-color: ${({ theme }) =>
                                    theme.colors.primary[400]};
                            }
                        `}
                        onClick={toggle}
                    >
                        <Box
                            as={ChevronDown}
                            size={rem(28)}
                            color="primary.1800"
                            transition="all 120ms ease"
                            transform={`rotate(${isCollapsed ? 180 : 0}deg)`}
                        />
                    </Box>
                    {!isCollapsed && (
                        <Grid>
                            <Box
                                gridColumn={{
                                    _: '1/-1',
                                    lg: '2/-2',
                                    xl: '3/-3',
                                }}
                                display="flex"
                                gap={{ _: 16, md: 20 }}
                                justifyContent="space-between"
                                flexDirection={{ _: 'column', md: 'row' }}
                            >
                                <Box>
                                    <Box display={{ _: 'none', md: 'block' }}>
                                        <Head6>Subscribe to our blog</Head6>
                                    </Box>
                                    <Box
                                        display={{ _: 'flex', md: 'none' }}
                                        justifyContent="space-between"
                                    >
                                        <Body1>Subscribe to our blog</Body1>
                                        <Box
                                            display="block"
                                            as={CloseIcon}
                                            size={rem(24)}
                                            color="primary.1800"
                                            onClick={() => setIsHidden(true)}
                                        />
                                    </Box>
                                    <Box display={{ _: 'none', md: 'block' }}>
                                        <Body1 mt={rem(12)}>
                                            Get the latest blog posts sent
                                            straight to your inbox.
                                        </Body1>
                                    </Box>
                                </Box>
                                <Box
                                    as="form"
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={rem(20)}
                                    onSubmit={handleSubmit(handleOnSubmit)}
                                >
                                    <Field
                                        id="email"
                                        width={{ _: '100%', md: rem('400px') }}
                                    >
                                        <Field.Input
                                            type="email"
                                            placeholder="name@internet.cool"
                                            ref={register({
                                                required: 'Email is required',
                                                pattern: {
                                                    value: EMAIL_REGEX,
                                                    message: 'Invalid Email',
                                                },
                                            })}
                                            error={errors.email}
                                            bgColor="neutral.white"
                                        />
                                        <Field.Hint
                                            color="primary.1900"
                                            display={{ _: 'none', md: 'block' }}
                                        >
                                            You can unsubscribe at anytime
                                        </Field.Hint>
                                        {errors.email && (
                                            <Field.ErrorMessage>
                                                {errors.email.message}
                                            </Field.ErrorMessage>
                                        )}
                                    </Field>
                                    <Button
                                        type="submit"
                                        variant="secondary"
                                        size="md"
                                        minWidth={rem(140)}
                                        loading={isLoading}
                                    >
                                        Subscribe
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </>
            )}
        </Box>
    );
};
